import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Send,
  Schedule,
  Link,
  CheckCircle,
  Error
} from '@mui/icons-material';
import socialMediaAPI from '../services/socialMediaAPI';

const SimplePosting = () => {
  const [postData, setPostData] = useState({
    message: '',
    mediaUrl: '',
    platform: '',
    pageId: '',
    scheduledTime: '',
    isScheduled: false
  });

  const [connectedAccounts, setConnectedAccounts] = useState({
    facebook: [],
    instagram: []
  });

  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadConnectedAccounts();
  }, []);

  const loadConnectedAccounts = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading connected accounts...');
      
      // Debug localStorage contents
      console.log('🔍 localStorage contents:');
      console.log('- facebookAccessToken:', localStorage.getItem('facebookAccessToken'));
      console.log('- facebookPages:', localStorage.getItem('facebookPages'));
      console.log('- facebookUser:', localStorage.getItem('facebookUser'));
      
      if (socialMediaAPI.isFacebookConnected()) {
        console.log('✅ Facebook is connected, fetching pages...');
        const accessToken = localStorage.getItem('facebookAccessToken');
        console.log('🔑 Access token available:', !!accessToken);
        
        let pages = await socialMediaAPI.getFacebookPages(accessToken);
        console.log('📘 Facebook pages loaded:', pages);
        
        // Fallback: if API fails, try to get pages from localStorage
        if (!pages || pages.length === 0) {
          console.log('🔄 API returned no pages, trying localStorage fallback...');
          const storedPages = localStorage.getItem('facebookPages');
          if (storedPages) {
            pages = JSON.parse(storedPages);
            console.log('📘 Facebook pages from localStorage:', pages);
          }
        }
        
        // Get Instagram accounts for each page
        const instagramAccounts = [];
        if (pages && Array.isArray(pages)) {
          console.log('🔄 Fetching Instagram accounts for', pages.length, 'pages...');
          for (const page of pages) {
            const instagram = await socialMediaAPI.getInstagramAccounts(page.id, page.access_token);
            instagramAccounts.push(...instagram);
          }
        }

        console.log('📷 Instagram accounts loaded:', instagramAccounts);

        setConnectedAccounts({
          facebook: pages || [],
          instagram: instagramAccounts || []
        });

        socialMediaAPI.saveConnectedAccounts({
          facebook: pages || [],
          instagram: instagramAccounts || []
        });
        
        console.log('✅ Connected accounts updated:', {
          facebook: pages?.length || 0,
          instagram: instagramAccounts?.length || 0
        });
      } else {
        console.log('❌ Facebook is not connected');
      }
    } catch (error) {
      console.error('❌ Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectFacebook = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      console.log('🔄 Starting Facebook connection...');
      
      // Debug current OAuth status
      socialMediaAPI.debugOAuthStatus();
      
      // Connect to Facebook
      const result = await socialMediaAPI.connectFacebook();
      
      if (result.success) {
        console.log('✅ Facebook connected, loading accounts...');
        
        // Debug: Check if token was stored immediately after OAuth
        console.log('🔍 Post-OAuth localStorage check:');
        console.log('- facebookAccessToken:', localStorage.getItem('facebookAccessToken'));
        console.log('- facebookPages:', localStorage.getItem('facebookPages'));
        console.log('- facebookUser:', localStorage.getItem('facebookUser'));
        
        // Load connected accounts
        await loadConnectedAccounts();
        
        setResult({ 
          success: true, 
          message: 'Facebook connected successfully! You can now post to your pages.' 
        });
      } else {
        throw new Error('Facebook connection failed');
      }
      
    } catch (error) {
      console.error('❌ Facebook connection error:', error);
      
      let errorMessage = error.message;
      
      // Provide more helpful error messages
      if (error.message.includes('popup')) {
        errorMessage = 'Popup was blocked. Please allow popups for this site and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Connection timed out. Please try again.';
      } else if (error.message.includes('cancelled')) {
        errorMessage = 'Connection was cancelled. Please try again.';
      }
      
      setResult({ 
        success: false, 
        message: `Facebook connection failed: ${errorMessage}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!postData.message.trim() || !postData.pageId) {
      setResult({ success: false, message: 'Please enter a message and select a platform.' });
      return;
    }

    setPosting(true);
    setResult(null);

    try {
      let response;
      
      if (postData.platform === 'facebook') {
        response = await socialMediaAPI.postToFacebook(
          postData.pageId,
          postData.message,
          postData.mediaUrl || null,
          postData.isScheduled ? postData.scheduledTime : null
        );
      } else if (postData.platform === 'instagram') {
        response = await socialMediaAPI.postToInstagram(
          postData.pageId,
          postData.message,
          postData.mediaUrl
        );
      }

      setResult({ success: true, message: response.message });
      setPostData({ ...postData, message: '', mediaUrl: '' }); // Clear form
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setPosting(false);
    }
  };

  const getPlatformOptions = () => {
    const options = [];
    
    if (connectedAccounts.facebook.length > 0) {
      options.push(...connectedAccounts.facebook.map(page => ({
        value: `facebook:${page.id}`,
        label: `Facebook: ${page.name}`,
        platform: 'facebook',
        pageId: page.id
      })));
    }
    
    if (connectedAccounts.instagram.length > 0) {
      options.push(...connectedAccounts.instagram.map(account => ({
        value: `instagram:${account.id}`,
        label: `Instagram: ${account.username}`,
        platform: 'instagram',
        pageId: account.id
      })));
    }
    
    return options;
  };

  const handlePlatformChange = (event) => {
    const selectedValue = event.target.value;
    const option = getPlatformOptions().find(opt => opt.value === selectedValue);
    
    if (option) {
      setPostData(prev => ({
        ...prev,
        platform: option.platform,
        pageId: option.pageId
      }));
    }
  };

  // Facebook Login Button callback function
  const checkLoginState = () => {
    if (window.FB) {
      window.FB.getLoginStatus(function(response) {
        console.log('Facebook Login Button callback:', response);
        
        if (response.status === 'connected') {
          console.log('User connected via Facebook Login Button');
          console.log('Access token:', response.authResponse.accessToken);
          console.log('User ID:', response.authResponse.userID);
          
          // Store Facebook access token
          localStorage.setItem('facebookAccessToken', response.authResponse.accessToken);
          localStorage.setItem('facebookUserID', response.authResponse.userID);
          
          // Load connected accounts
          loadConnectedAccounts();
          
          setResult({ 
            success: true, 
            message: 'Facebook connected successfully via Login Button! You can now post to your pages.' 
          });
          
        } else {
          console.log('User not connected via Facebook Login Button');
          setResult({ 
            success: false, 
            message: 'Facebook connection failed. Please try again.' 
          });
        }
      });
    } else {
      console.log('Facebook SDK not loaded');
    }
  };

  // Make checkLoginState available globally for the Facebook Login Button
  useEffect(() => {
    window.checkLoginState = checkLoginState;
  }, []);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Send color="primary" />
        Social Media Posting
      </Typography>

      {/* Connection Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connected Accounts
          </Typography>
          
          {!socialMediaAPI.isFacebookConnected() ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Connect your Facebook account to post to Facebook and Instagram.
              </Alert>
              
              {/* Facebook Login Button */}
              <div 
                className="fb-login-button" 
                data-width="" 
                data-size="large" 
                data-button-type="continue_with" 
                data-layout="rounded" 
                data-auto-logout-link="false" 
                data-use-continue-as="false"
                data-scope="pages_show_list,pages_read_engagement,pages_manage_posts"
                onlogin="checkLoginState();"
              ></div>
              
              {/* Instagram Connection Button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Instagram uses the same Facebook OAuth but with different scope
                    const instagramAuthUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=1259950398921712&redirect_uri=${encodeURIComponent('https://lpds.netlify.app/facebook-callback.html')}&scope=instagram_basic,instagram_content_publish&state=instagram`;
                    window.open(instagramAuthUrl, 'instagram-auth', 'width=600,height=600');
                  }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Instagram />}
                  sx={{ 
                    backgroundColor: '#E4405F', 
                    '&:hover': { backgroundColor: '#C13584' },
                    color: 'white'
                  }}
                >
                  {loading ? 'Connecting...' : 'Connect Instagram'}
                </Button>
              </Box>
              
              {/* Fallback Button */}
              <Button
                variant="contained"
                onClick={handleConnectFacebook}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Facebook />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Connecting...' : 'Connect Facebook (Alternative)'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ✅ Facebook Connected Successfully!
                </Typography>
                <Typography variant="body2">
                  Connected to {connectedAccounts.facebook.length} Facebook page(s) and {connectedAccounts.instagram.length} Instagram account(s)
                </Typography>
              </Alert>
              
              {/* Show Connected Pages */}
              {connectedAccounts.facebook.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    📘 Connected Facebook Pages:
                  </Typography>
                  {connectedAccounts.facebook.map((page, index) => (
                    <Chip
                      key={page.id}
                      label={`${page.name} (${page.category})`}
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                      icon={<Facebook />}
                    />
                  ))}
                </Box>
              )}
              
              {/* Show Connected Instagram Accounts */}
              {connectedAccounts.instagram.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    📷 Connected Instagram Accounts:
                  </Typography>
                  {connectedAccounts.instagram.map((account, index) => (
                    <Chip
                      key={account.id}
                      label={`@${account.username}`}
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                      icon={<Instagram />}
                    />
                  ))}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={loadConnectedAccounts}
                  disabled={loading}
                  startIcon={<Link />}
                >
                  Refresh Accounts
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const status = socialMediaAPI.debugOAuthStatus();
                    console.log('Debug status:', status);
                    setResult({ 
                      success: true, 
                      message: `Debug: Token=${status.hasToken}, Pages=${status.hasPages}, User=${status.hasUser}` 
                    });
                  }}
                  size="small"
                >
                  Debug OAuth
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    localStorage.removeItem('facebookAccessToken');
                    localStorage.removeItem('facebookPages');
                    localStorage.removeItem('facebookUser');
                    setConnectedAccounts({ facebook: [], instagram: [] });
                    setResult({ 
                      success: true, 
                      message: 'Facebook disconnected successfully!' 
                    });
                  }}
                  size="small"
                >
                  Disconnect
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Post Form */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create Post
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Platform Selection */}
            <FormControl fullWidth>
              <InputLabel>Select Platform</InputLabel>
              <Select
                value={postData.platform && postData.pageId ? `${postData.platform}:${postData.pageId}` : ''}
                onChange={handlePlatformChange}
                label="Select Platform"
              >
                {getPlatformOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Message */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Post Message"
              value={postData.message}
              onChange={(e) => setPostData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="What would you like to post?"
            />

            {/* Media URL */}
            <TextField
              fullWidth
              label="Media URL (optional)"
              value={postData.mediaUrl}
              onChange={(e) => setPostData(prev => ({ ...prev, mediaUrl: e.target.value }))}
              placeholder="https://example.com/image.jpg"
            />

            {/* Scheduling */}
            <FormControlLabel
              control={
                <Switch
                  checked={postData.isScheduled}
                  onChange={(e) => setPostData(prev => ({ ...prev, isScheduled: e.target.checked }))}
                />
              }
              label="Schedule Post"
            />

            {postData.isScheduled && (
              <TextField
                fullWidth
                type="datetime-local"
                label="Schedule Time"
                value={postData.scheduledTime}
                onChange={(e) => setPostData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            )}

            <Divider />

            {/* Post Button */}
            <Button
              variant="contained"
              size="large"
              onClick={handlePost}
              disabled={posting || !postData.message.trim() || !postData.pageId}
              startIcon={posting ? <CircularProgress size={20} /> : <Send />}
              sx={{ mt: 2 }}
            >
              {posting ? 'Posting...' : postData.isScheduled ? 'Schedule Post' : 'Post Now'}
            </Button>
          </Box>

          {/* Result */}
          {result && (
            <Alert 
              severity={result.success ? 'success' : 'error'} 
              sx={{ mt: 2 }}
              icon={result.success ? <CheckCircle /> : <Error />}
            >
              {result.message}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SimplePosting;
